import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { generateEnumTranslations } from '@/common/utils/enum'
import * as UI from '@/components/componentLibrary'
import { SpatialPolicy as Policy } from '@/models/spatialPolicy'
import { generateEnumDropdownOptions } from '@/models/spatialPolicyTypes'
import { VariableDuration, VariablePriceDuration } from '@/models/variablePriceDuration'
import {
  Day,
  DayEnum,
  VariablePriceSchedule,
  VariableSchedule,
} from '@/models/variablePriceSchedule'
import { EditPolicy } from '@/modules/policiesV1/policyLibrary/policyEditController'
import { useCurrentRegion } from '@/modules/urlRouting/hooks'

import { DropdownWithTags } from '../../components'

import './index.scss'

interface TimeTableProps {
  editable?: boolean
  editPolicy?: EditPolicy
  policy: Policy
}

interface VariableRowProps {
  editable?: boolean
  hideClose: boolean
  onRemove?: () => void
}

interface VariableScheduleRowProps extends VariableRowProps {
  pricingData: VariableSchedule
  // applies an edit to this row's fields on the active variable-price-schedule sub-model
  editRow: (mutate: (row: VariableSchedule) => void) => void
}

interface VariableDurationRowProps extends VariableRowProps {
  pricingData: VariableDuration
  // applies an edit to this row's fields on the active variable-price-duration sub-model
  editRow: (mutate: (row: VariableDuration) => void) => void
}

const Header = ({
  editable,
  suffix,
  text,
}: {
  suffix: 'duration' | 'schedule'
  text: string[]
  editable?: boolean
}) => {
  return (
    <div className={classNames('timetable-row', suffix, !editable && 'uneditable')}>
      {text.map((val: string) => (
        <UI.Text key={val} styleType="caption">
          {val}
        </UI.Text>
      ))}
    </div>
  )
}

const closeIcon = (onRemove?: () => void) => (
  <UI.Icon
    className="time-table-close-icon"
    color="black-1"
    icon="CloseRounded"
    onClick={onRemove}
  />
)

const VariableScheduleRow = ({
  editable,
  editRow,
  hideClose,
  onRemove,
  pricingData,
}: VariableScheduleRowProps) => {
  const {
    data: { currencyCode },
  } = useCurrentRegion()
  const { day, startTime, endTime, price } = pricingData
  return (
    <div className={classNames('timetable-row schedule', !editable && 'uneditable')}>
      <UI.TimeInput
        className="timetable-input-small"
        editable={editable}
        value={startTime || undefined}
        onAfterChange={t => editRow(row => (row.startTime = t || null))}
      />
      <UI.TimeInput
        className="timetable-input-small"
        editable={editable}
        value={endTime || undefined}
        onAfterChange={t => editRow(row => (row.endTime = t || null))}
      />
      <UI.CurrencyInput
        className="timetable-input-small"
        currencyCode={currencyCode}
        editable={editable}
        value={price === 0 ? '' : price.toString()}
        onChange={price => editRow(row => (row.price = Number(price) || 0))}
      />
      {
        <DropdownWithTags
          className="timetable-input-days"
          editable={editable}
          onChange={day => editRow(row => (row.day = day as Day))}
          options={generateEnumDropdownOptions(DayEnum, 'DaysOfWeek')}
          placeholder="Days of the Week"
          selectedValue={generateEnumTranslations(DayEnum, 'DayEnum')[day]}
          value={day}
        />
      }
      {!hideClose && closeIcon(onRemove)}
    </div>
  )
}

const VariableDurationRow = ({
  editable,
  editRow,
  pricingData,
  hideClose,
  onRemove,
}: VariableDurationRowProps) => {
  const {
    data: { currencyCode },
  } = useCurrentRegion()
  const { endDuration, price, startDuration } = pricingData
  return (
    <div className={classNames('timetable-row duration', !editable && 'uneditable')}>
      <UI.Input
        className="timetable-input-small"
        editable={editable}
        icon="Clock"
        value={startDuration || startDuration === 0 ? startDuration : ''}
        // @ts-ignore
        onChange={(e, data) => {
          const parsed = parseInt(data.value, 10)
          editRow(row => (row.startDuration = Number.isNaN(parsed) ? null : parsed))
        }}
      />
      <UI.Input
        className="timetable-input-small"
        editable={editable}
        icon="Clock"
        value={endDuration || endDuration === 0 ? endDuration : ''}
        // @ts-ignore
        onChange={(e, data) => {
          const parsed = parseInt(data.value, 10)
          editRow(row => (row.endDuration = Number.isNaN(parsed) ? null : parsed))
        }}
      />
      <UI.CurrencyInput
        className="timetable-input-small"
        currencyCode={currencyCode}
        editable={editable}
        value={price === 0 ? '' : price.toString()}
        onChange={price => editRow(row => (row.price = Number(price) || 0))}
      />
      {!hideClose && closeIcon(onRemove)}
    </div>
  )
}

export const TimeTable = ({ editable = true, editPolicy, policy }: TimeTableProps) => {
  const { t } = useTranslation()

  const { parkingFeeType, variablePriceDuration, variablePriceSchedule } = policy
  if (
    (parkingFeeType === 'variable_price_schedule' && variablePriceSchedule) ||
    (parkingFeeType === 'variable_price_parking_duration' && variablePriceDuration)
  ) {
    const isSchedule = parkingFeeType === 'variable_price_schedule'
    const dataModel = isSchedule ? variablePriceSchedule! : variablePriceDuration!

    return (
      <div className="variable-pricing-timetable">
        <Header
          editable={editable}
          suffix={isSchedule ? 'schedule' : 'duration'}
          text={
            isSchedule
              ? [
                  t('policiesLibrary.formStartTime', 'Start Time (24hr)'),
                  t('policiesLibrary.formEndTime', 'End Time (24hr)'),
                  t('policiesLibrary.formPricePerHour', 'Price (Per Hour)'),
                ]
              : [
                  t('policiesLibrary.formStartTimeMin', 'Start Time (min)'),
                  t('policiesLibrary.formEndTimeMin', 'End Time (min)'),
                  t('policiesLibrary.formPricePerHour', 'Price (Per Hour)'),
                ]
          }
        />
        {dataModel.prices.map((data, idx) => (
          <Fragment key={`fragment-${idx}`}>
            {isSchedule ? (
              <VariableScheduleRow
                editable={editable}
                editRow={mutate =>
                  editPolicy?.(p => {
                    const row = (p.variablePrices as VariablePriceSchedule)?.prices[idx]
                    if (row) mutate(row)
                  })
                }
                pricingData={data as VariableSchedule}
                hideClose={idx === 0 || !editable}
                key={idx}
                onRemove={() => editPolicy?.(p => p.variablePrices?.remove(idx))}
              />
            ) : (
              <VariableDurationRow
                editable={editable}
                editRow={mutate =>
                  editPolicy?.(p => {
                    const row = (p.variablePrices as VariablePriceDuration)?.prices[idx]
                    if (row) mutate(row)
                  })
                }
                pricingData={data as VariableDuration}
                hideClose={idx === 0 || !editable}
                key={idx}
                onRemove={() => editPolicy?.(p => p.variablePrices?.remove(idx))}
              />
            )}
            {idx !== dataModel.prices.length - 1 && !editable && (
              <UI.Divider key={`divider-${idx}`} className="uneditable-divider" />
            )}
          </Fragment>
        ))}
        {editable && (
          <UI.Button
            fluid
            icon="Expand"
            onClick={e => {
              e.preventDefault()
              editPolicy?.(p => p.variablePrices?.add())
            }}
            secondary
            text={t('policiesLibrary.formAddTimeFrame', 'Add Time Frame')}
          />
        )}
      </div>
    )
  }
  return null
}
